import { supabase } from '@/shared/lib/supabase/client';
import { AppError } from '@/shared/lib/repository/errors';
import { buildTree } from '../model/tree';
import type { CommentAuthor, CommentNode } from '../model/types';

const AUTHOR_EMBED =
  'author:profiles!comments_author_id_fkey(id, name, initials, avatar_color, avatar_url)';
const SELECT = `id, body, created_at, parent_id, comment_likes(user_id), ${AUTHOR_EMBED}`;

type ProfileLite = {
  id: string;
  name: string;
  initials: string;
  avatar_color: string;
  avatar_url: string | null;
};
interface CommentRow {
  id: string;
  body: string;
  created_at: string;
  parent_id: string | null;
  comment_likes: { user_id: string }[] | null;
  author: ProfileLite | ProfileLite[] | null;
}

function normalizeAuthor(author: CommentRow['author']): CommentAuthor | null {
  const p = Array.isArray(author) ? (author[0] ?? null) : author;
  if (!p) return null;
  return {
    id: p.id,
    name: p.name,
    initials: p.initials,
    avatarColor: p.avatar_color,
    avatarUrl: p.avatar_url,
  };
}

function mapComment(row: CommentRow, userId?: string): CommentNode {
  const likes = row.comment_likes ?? [];
  return {
    id: row.id,
    body: row.body,
    createdAt: row.created_at,
    parentId: row.parent_id,
    author: normalizeAuthor(row.author),
    likeCount: likes.length,
    likedByMe: userId ? likes.some((like) => like.user_id === userId) : false,
    replies: [],
  };
}

/** Comments + comment likes. RLS: public read on published, insert own, likes public read. */
export const commentsRepository = {
  async listForArticle(articleId: string, userId?: string): Promise<CommentNode[]> {
    const { data, error } = await supabase
      .from('comments')
      .select(SELECT)
      .eq('article_id', articleId)
      .order('created_at', { ascending: true });
    if (error) throw new AppError(error.message, error.code, error);

    const nodes = ((data ?? []) as unknown as CommentRow[]).map((row) => mapComment(row, userId));
    return buildTree(nodes);
  },

  async add(input: {
    articleId: string;
    authorId: string;
    body: string;
    parentId?: string | null;
  }): Promise<void> {
    const { error } = await supabase.from('comments').insert({
      article_id: input.articleId,
      author_id: input.authorId,
      body: input.body,
      parent_id: input.parentId ?? null,
    });
    if (error) throw new AppError(error.message, error.code, error);
  },

  async like(commentId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('comment_likes')
      .insert({ comment_id: commentId, user_id: userId });
    if (error) throw new AppError(error.message, error.code, error);
  },

  async unlike(commentId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('comment_likes')
      .delete()
      .eq('comment_id', commentId)
      .eq('user_id', userId);
    if (error) throw new AppError(error.message, error.code, error);
  },
};
